import * as React from 'react';

import {
  ABMapping,
  DefaultValues,
  GlanceRegionPublish,
  PartnerSource,
  RegionPublishMeta,
  StartTimeType,
  UserLevelDefaultValues,
} from './GlanceRegionPublish';
import {
  AbsoluteTime,
  Duration,
  DurationUnit,
  Experiment,
  ExperimentMeta,
  ExperimentVariant,
  GlanceDraft,
  GlanceMeta,
  GlanceTime,
  GlanceType,
  Partner,
  Region,
  RelativeTime,
  SegmentTargetingRule,
  StartTime,
  StudioAppConfig,
} from 'glance-datatypes';
import { Tab, Tabs } from 'material-ui/Tabs';
import Utils, {
  allRegions,
  MONTHS,
  regionNames,
  stateFilters,
} from '../../../Utils';

import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import Constants, { Partners } from '../../../Constants';
import RestService from '../../../RestService';
import Snackbar from 'material-ui/Snackbar';
import { FontIcon } from 'material-ui';
import { red500, amberA700 } from 'material-ui/styles/colors';
import { response } from 'express';

const GIONEE_PARTNER_ID = '59458435b8034886b62dbe672e010e064ab5aecf';
const styles = {
  width100: {
    width: '100%',
    maxWidth: 'none',
  } as React.CSSProperties,
  width50: {
    width: '50%',
    maxWidth: 'none',
  } as React.CSSProperties,
  dataTimeStyle: {
    display: 'inline-block',
    width: '50%',
  } as React.CSSProperties,
  inputStyle: {
    color: '#505050',
    fontSize: '0.9em',
  } as React.CSSProperties,
  region: {
    marginLeft: '2px auto',
    display: 'inline-block',
  } as React.CSSProperties,
  regionTab: {
    minWidth: '70px',
    fontSize: '12px',
  } as React.CSSProperties,
  wrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  } as React.CSSProperties,
  container: {
    backgroundColor: '#FFE59E',
    padding: '19px',
    borderRadius: '4px',
    width: '386px',
  } as React.CSSProperties,
  warningText: {
    fontSize: '14px',
    fontWeight: 'normal',
    lineHeight: '160%',
    color: '#000000',
    marginBottom: '20px',
  } as React.CSSProperties,
  warningTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    lineHeight: '160%',
    color: '#000000',
  } as React.CSSProperties,
  warningHeader: {
    display: 'flex',
    marginBottom: '8px',
  } as React.CSSProperties,
  warningIcon: {
    fontSize: '24px',
    marginRight: '8px',
  } as React.CSSProperties,
};

function regionSort(a: Region, b: Region) {
  if (a > b) {
    return -1;
  } else {
    return 1;
  }
}

interface Props {
  open: boolean;
  studioAppConf: StudioAppConfig;
  closeDialog: () => any;
  onPublishDone: (draft: GlanceDraft) => any;
  draft: GlanceDraft;
  experiments: Map<string, Experiment>;
}

enum LoadState {
  Loading,
  Loaded,
  Failed,
}

interface State {
  loadState: LoadState;
  regionPublishMeta?: Map<Region, RegionPublishMeta>;
  shortTitleCheck: boolean;
  cheesySummaryCheck: boolean;
  snackBarOpen?: boolean;
  snackBarMessage?: string;
  publishInProgress: boolean;
  segmentRules: SegmentTargetingRule[];
}

class GlancePublishComponent extends React.Component<Props, State> {
  defaultValues: DefaultValues = {
    durationUnit: DurationUnit.DAYS,
    duration: 2,
    startTimeType: StartTimeType.NOW,
    startDate: new Date(),
    startTime: new Date(),
  };

  userLevelDefaultValues: UserLevelDefaultValues = {
    enabled: true,
    durationUnit: DurationUnit.DAYS,
    duration: 1,
  };

  partnerMap: Map<string, Partner> = new Map<string, Partner>();
  activeRegion: Region;
  sortedPreferedRegions: Region[];

  constructor(props: Props) {
    super(props);

    let sortedPreferedRegions: Region[] = null;
    let defaultRegion: Region = null;
    if (this.props.draft.preferedRegions) {
      sortedPreferedRegions = this.props.draft.preferedRegions.sort((a, b) =>
        regionSort(a, b),
      );
      defaultRegion = sortedPreferedRegions[0];
    }

    const partnerSources: Map<Region, PartnerSource[]> = new Map<
      Region,
      PartnerSource[]
    >();
    const abPartitions: Map<Region, ABMapping[]> = new Map<
      Region,
      ABMapping[]
    >();
    const partnerMap: Map<string, Partner> = this.partnerMap;
    this.props.studioAppConf.partners.forEach((partner) => {
      partnerMap.set(partner.partnerId, partner);
    });

    allRegions.forEach((region) => {
      partnerSources.set(region, []);
      abPartitions.set(region, []);
    });

    this.props.studioAppConf.partnerMetas.forEach((item) => {
      const partner = partnerMap.get(item.partnerId);
      item.supportedRegions.forEach((region) => {
        partnerSources.set(region, [
          ...partnerSources.get(region),
          {
            text: partner.name,
            value: partner,
          },
        ]);
      });
      item.abConfigs
        ? item.abConfigs.forEach((abConfig) => {
            abPartitions.set(abConfig.region, [
              ...abPartitions.get(abConfig.region),
              {
                partnerId: partner.partnerId,
                abTag: abConfig.id,
              },
            ]);
          })
        : '';
    });

    const regionPublishMeta: Map<Region, RegionPublishMeta> = new Map<
      Region,
      RegionPublishMeta
    >();
    allRegions.forEach((region) => {
      regionPublishMeta.set(region, {
        partnerSources: partnerSources.get(region),
        abMappings: abPartitions.get(region),
      });
    });

    const segmentRules = ([] = this.props.studioAppConf.segmentTargetingRule);

    this.state = {
      loadState: LoadState.Loading,
      regionPublishMeta: regionPublishMeta,
      snackBarOpen: false,
      snackBarMessage: '',
      shortTitleCheck: true,
      cheesySummaryCheck: true,
      publishInProgress: false,
      segmentRules,
    };
    this.activeRegion = defaultRegion;

    this.handleActiveRegion = this.handleActiveRegion.bind(this);

    this.handlerPartnerAdd = this.handlerPartnerAdd.bind(this);
    this.handlerPartnerDelete = this.handlerPartnerDelete.bind(this);
    this.handlerABPartitionAdd = this.handlerABPartitionAdd.bind(this);
    this.handlerABParitionDelete = this.handlerABParitionDelete.bind(this);
    this.durationUpdate = this.durationUpdate.bind(this);
    this.onUserLevelDurationUpdate = this.onUserLevelDurationUpdate.bind(this);
    this.onPartingTimeUpdate = this.onPartingTimeUpdate.bind(this);
    this.startTimeUpdate = this.startTimeUpdate.bind(this);
    this.onPublishConfirm = this.onPublishConfirm.bind(this);
    this.closeSnackbar = this.closeSnackbar.bind(this);
    this.updateData = this.updateData.bind(this);

    this.handleExperimentVariantSelected = this.handleExperimentVariantSelected.bind(
      this,
    );
    this.handleExperimentSelected = this.handleExperimentSelected.bind(this);
    this.handleExperimentVariantDelete = this.handleExperimentVariantDelete.bind(
      this,
    );
  }

  componentDidMount() {
    this.updateData();
  }

  updateData() {
    let that = this;
    that.setState({
      ...that.state,
      loadState: LoadState.Loading,
    });
    RestService.getPublishMeta(
      this.props.draft.draftId,
      (metas: GlanceMeta[]) => {
        if (metas == null) {
          that.setState({
            ...that.state,
            loadState: LoadState.Failed,
          });
        } else {
          const regionPublishMeta = this.state.regionPublishMeta;

          metas.forEach((meta) => {
            const publishMeta = regionPublishMeta.get(meta.region);
            let regionPublishedTo = publishMeta.publishedTo;
            if (!regionPublishedTo) {
              regionPublishedTo = [];
            }
            const partner = this.partnerMap.get(meta.partnerId);
            regionPublishedTo.push(partner);

            const partnerSources = publishMeta.partnerSources.filter(
              (partnerSource) => {
                return regionPublishedTo.indexOf(partnerSource.value) == -1;
              },
            );

            regionPublishMeta.set(meta.region, {
              ...publishMeta,
              publishedTo: regionPublishedTo,
              partnerSources: partnerSources,
              publishTo: [],
            });
          });
          that.setState({
            ...that.state,
            regionPublishMeta: regionPublishMeta,
            loadState: LoadState.Loaded,
          });
        }
      },
    );
  }

  handlerPartnerAdd(chip: PartnerSource) {
    if (chip.value.partnerId == GIONEE_PARTNER_ID && this.props.draft.peek) {
      if (!this.props.draft.peek.shortTitle) {
        this.state.shortTitleCheck = false;
        this.setState({
          ...this.state,
        });
        return;
      }
      if (!this.props.draft.peek.cheesySummary) {
        this.state.cheesySummaryCheck = false;
        this.setState({
          ...this.state,
        });
        return;
      }
    }

    const regionPublishMeta = this.state.regionPublishMeta;
    const currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    let publishToPartners: PartnerSource[] = currentRegionPublishMeta.publishTo;
    if (!publishToPartners) {
      publishToPartners = [];
    }
    if (
      publishToPartners.indexOf(chip) == -1 &&
      currentRegionPublishMeta.partnerSources.indexOf(chip) != -1
    ) {
      this.state.shortTitleCheck = true;
      this.state.cheesySummaryCheck = true;
      publishToPartners = [...publishToPartners, chip];

      let newCurrentRegionPublishMeta: RegionPublishMeta = {
        ...currentRegionPublishMeta,
        publishTo: publishToPartners,
        publishToSelected: chip.value.partnerId,
      };

      if (
        publishToPartners.length != 1 ||
        !(
          publishToPartners[0].value.partnerId ==
            Partners.SAMSUNG_ANDROID_PARTNER_ID ||
          publishToPartners[0].value.partnerId == Partners.TEST_PARTNER_ID
        )
      ) {
        newCurrentRegionPublishMeta = {
          ...newCurrentRegionPublishMeta,
          userLevelDurationEnabled: false,
          userLevelDuration: undefined,
          userLevelDurationUnit: undefined,
        };
      }

      regionPublishMeta.set(this.activeRegion, newCurrentRegionPublishMeta);
      this.setState({
        ...this.state,
        regionPublishMeta: regionPublishMeta,
      });
    }
  }

  handleExperimentSelected(experimentId: string) {
    const regionPublishMeta = this.state.regionPublishMeta;
    let currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    let selectedExperimentMeta: ExperimentMeta = null;
    if (currentRegionPublishMeta.selectedExperiments) {
      selectedExperimentMeta = currentRegionPublishMeta.selectedExperiments[0];
    }

    let experiments = [];
    if (experimentId) {
      const selectedExperiment = this.props.experiments.get(experimentId);
      experiments = [
        {
          ...selectedExperimentMeta,
          id: experimentId,
          variantId: selectedExperimentMeta
            ? selectedExperimentMeta.variantId
            : selectedExperiment.control.id,
        },
      ];
    }

    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      selectedExperiments: experiments,
      selectedExperimentMetas: [...experiments],
    };
    regionPublishMeta.set(this.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  }

  handleExperimentVariantSelected(variantId: string, experimentId: string) {
    const regionPublishMeta = this.state.regionPublishMeta;
    let currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    let selectedExperiment: ExperimentMeta = null;
    let selectedExperimentMetas =
      currentRegionPublishMeta.selectedExperimentMetas;
    selectedExperiment = {
      id: experimentId,
      variantId: variantId,
    };

    const alreadyExists = currentRegionPublishMeta.selectedExperimentMetas.indexOf(
      selectedExperiment,
    );

    if (alreadyExists == -1) {
      selectedExperimentMetas.push(selectedExperiment);
    }

    selectedExperiment = {
      ...selectedExperiment,
      variantId: variantId,
    };

    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      selectedExperimentMetas: selectedExperimentMetas,
    };

    regionPublishMeta.set(this.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  }

  handleExperimentVariantDelete(chip: ExperimentVariant, experimentId: String) {
    const regionPublishMeta = this.state.regionPublishMeta;
    let currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    const selectedExperiments = currentRegionPublishMeta.selectedExperimentMetas.filter(
      (item) => {
        return item.variantId != chip.id;
      },
    );

    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      selectedExperimentMetas: selectedExperiments,
    };

    regionPublishMeta.set(this.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  }

  handlerABPartitionAdd(chip: ABMapping) {
    const regionPublishMeta = this.state.regionPublishMeta;
    let currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    let currentABSelectedMappings = currentRegionPublishMeta.abSelectedMappings;
    let currentABSelected = currentRegionPublishMeta.abSelected;

    if (!currentABSelectedMappings) {
      currentABSelectedMappings = [];
    }
    if (!currentABSelected) {
      currentABSelected = [];
    }

    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      abSelected: [...currentABSelected, chip],
      abSelectedMappings: [...currentABSelectedMappings, chip],
    };
    regionPublishMeta.set(this.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  }

  handlerPartnerDelete(chip: Partner) {
    const regionPublishMeta = this.state.regionPublishMeta;
    const currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    if (!currentRegionPublishMeta.publishTo) {
      return;
    }
    const publishToPartners = currentRegionPublishMeta.publishTo.filter(
      (item) => {
        return item.value.partnerId != chip.partnerId;
      },
    );
    const newCurrentRegionPublishMeta: RegionPublishMeta = {
      ...currentRegionPublishMeta,
      publishTo: publishToPartners,
      publishToSelected: null,
    };
    regionPublishMeta.set(this.activeRegion, newCurrentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  }

  handlerABParitionDelete(chip: ABMapping) {
    const regionPublishMeta = this.state.regionPublishMeta;
    let currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    let newABSelectedMappings = currentRegionPublishMeta.abSelectedMappings.filter(
      (abMapping) => abMapping != chip,
    );
    let newABSelected = currentRegionPublishMeta.abSelected.filter(
      (abMapping) => abMapping != chip,
    );
    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      abSelected: newABSelected,
      abSelectedMappings: newABSelectedMappings,
    };
    regionPublishMeta.set(this.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  }

  onPublishConfirm() {
    this.setState({
      ...this.state,
      publishInProgress: true,
    });
    console.log('publish', this.state.regionPublishMeta.get(this.activeRegion));
    let publishMeta = this.state.regionPublishMeta.get(this.activeRegion);
    if (
      publishMeta.selectedExperiments &&
      publishMeta.selectedExperiments.length > 0 &&
      publishMeta.selectedExperimentMetas.length == 0
    ) {
      this.setState({
        ...this.state,
        snackBarOpen: true,
        snackBarMessage: 'Select at least 1 variant for publishing',
      });
      return;
    }

    let startTimeObj: StartTime;
    const startTimeType = publishMeta.startTimeType
      ? publishMeta.startTimeType
      : this.defaultValues.startTimeType;
    if (startTimeType == StartTimeType.NOW) {
      startTimeObj = new StartTime({
        absoluteTime: new AbsoluteTime({
          timeInSecs: Math.round(new Date().getTime() / 1000),
        }),
      });
    } else {
      const startDate = publishMeta.startDate
        ? publishMeta.startDate
        : this.defaultValues.startDate;
      const startTime = publishMeta.startTime
        ? publishMeta.startTime
        : this.defaultValues.startTime;
      startTimeObj = new StartTime({
        relativeTime: new RelativeTime({
          year: startDate.getFullYear(),
          month: startDate.getMonth() + 1,
          date: startDate.getDate(),
          hour: startTime.getHours(),
          minute: startTime.getMinutes(),
        }),
      });
    }

    const userLevelDuration = this.userLevelDefaultValues.enabled
      ? new Duration({
          unit: this.userLevelDefaultValues.durationUnit,
          length: this.userLevelDefaultValues.duration,
        })
      : null;

    const glanceTime: GlanceTime = new GlanceTime({
      duration: new Duration({
        unit: publishMeta.durationUnit
          ? publishMeta.durationUnit
          : this.defaultValues.durationUnit,
        length: publishMeta.duration
          ? publishMeta.duration
          : this.defaultValues.duration,
      }),
      startTime: startTimeObj,
      userLevelDuration: userLevelDuration,
    });

    const partners: Partner[] = [];
    publishMeta.publishTo.forEach((source) => {
      partners.push(source.value);
    });
    let that = this;
    const partnerIds = partners.map((partner) => partner.partnerId);
    const hasProlongedExpiry =
      this.props.draft.type == GlanceType.STORY &&
      !Utils.isExpiryRelaxed(partnerIds) &&
      ((glanceTime.duration.unit == DurationUnit.DAYS &&
        glanceTime.duration.length > 30) ||
        (glanceTime.duration.unit == DurationUnit.HOURS &&
          glanceTime.duration.length > 720));
    const isContentCards = this.props.draft.categoryIds.includes(
      Constants.CONTENT_FOR_APPS,
    );
    const userName = this.props.studioAppConf.currentToken.user.email.split(
      '@',
    )[0];
    const isContentCardAdmin =
      isContentCards && Constants.CONTENT_CARDS_ADMINS.includes(userName);
    // don't show error is type is story, category is '#content_for_apps' & user is part of content card admins
    if (hasProlongedExpiry && !isContentCardAdmin) {
      if (
        this.props.draft.frequencyCaps == null ||
        this.props.draft.frequencyCaps.weekly == null ||
        this.props.draft.frequencyCaps.weekly > 5
      ) {
        that.setState({
          ...that.state,
          snackBarOpen: true,
          snackBarMessage:
            'Duration Cannot be more than 72 hours for glances without frequency cap',
        });
        return;
      }
    }

    RestService.publishDraft(
      this.props.draft.draftId,
      this.activeRegion,
      partners,
      glanceTime,
      publishMeta.selectedExperimentMetas,
      publishMeta.selectedSegmentRulesList,
      publishMeta.isPartedGlance,
      publishMeta.partingSchedule,
      (result, error) => {
        if (result) {
          that.setState({
            ...that.state,
            snackBarOpen: true,
            publishInProgress: false,
            snackBarMessage: 'Glance Published Successfully',
          });
        }
        if (error) {
          that.setState({
            ...that.state,
            snackBarOpen: true,
            publishInProgress: false,
            snackBarMessage: 'Glance Publishing Failed',
          });
        }
        that.updateData();
      },
    );
  }

  handleActiveRegion(region: Region) {
    this.activeRegion = region;
    this.setState({
      ...this.state,
    });
  }

  durationUpdate(unit: DurationUnit, value: number) {
    const regionPublishMeta = this.state.regionPublishMeta;
    const currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    const newCurrentRegionPublishMeta: RegionPublishMeta = {
      ...currentRegionPublishMeta,
      duration: value,
      durationUnit: unit,
    };
    regionPublishMeta.set(this.activeRegion, newCurrentRegionPublishMeta);
    this.defaultValues = {
      ...this.defaultValues,
      duration: value,
      durationUnit: unit,
    };
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  }

  onUserLevelDurationUpdate(
    enabled: boolean,
    unit?: DurationUnit,
    value?: number,
  ) {
    const regionPublishMeta = this.state.regionPublishMeta;
    const currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    if (!enabled) {
      unit = null;
      value = null;
    }
    this.userLevelDefaultValues = {
      enabled: enabled,
      duration: value,
      durationUnit: unit,
    };
    const newCurrentRegionPublishMeta: RegionPublishMeta = {
      ...currentRegionPublishMeta,
      userLevelDuration: value,
      userLevelDurationUnit: unit,
      userLevelDurationEnabled: enabled,
    };
    regionPublishMeta.set(this.activeRegion, newCurrentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  }

  startTimeUpdate(type: StartTimeType, startDate: Date, startTime: Date) {
    const regionPublishMeta = this.state.regionPublishMeta;
    const currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    const newCurrentRegionPublishMeta: RegionPublishMeta = {
      ...currentRegionPublishMeta,
      startTimeType: type,
      startDate: startDate,
      startTime: startTime,
    };
    regionPublishMeta.set(this.activeRegion, newCurrentRegionPublishMeta);
    this.defaultValues = {
      ...this.defaultValues,
      startTimeType: type,
      startDate: startDate,
      startTime: startTime,
    };
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  }

  closeSnackbar() {
    this.setState({
      ...this.state,
      snackBarOpen: false,
    });
  }

  onTargetingRuleDeleted = (index) => {
    const regionPublishMeta = this.state.regionPublishMeta;
    let currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    let {
      selectedSegmentRulesList: selectedSegmentRulesNew = [],
    } = currentRegionPublishMeta;
    selectedSegmentRulesNew.splice(index, 1);
    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      selectedSegmentRulesList: selectedSegmentRulesNew,
    };
    regionPublishMeta.set(this.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  onSegmentRuleSelected = (value, isAdd, index) => {
    const regionPublishMeta = this.state.regionPublishMeta;
    let currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    let {
      selectedSegmentRulesList: selectedSegmentRulesNew = [],
    } = currentRegionPublishMeta;
    if (isAdd) {
      if (selectedSegmentRulesNew[index]) {
        selectedSegmentRulesNew[index].push(value);
      } else {
        selectedSegmentRulesNew[index] = [value];
      }
    } else {
      selectedSegmentRulesNew[index] = selectedSegmentRulesNew[index].filter(
        (selSeg) => selSeg.ruleId !== value,
      );
    }
    currentRegionPublishMeta = {
      ...currentRegionPublishMeta,
      selectedSegmentRulesList: selectedSegmentRulesNew,
    };
    regionPublishMeta.set(this.activeRegion, currentRegionPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  onSegmentRuleFromGambit = (rule) => {
    this.setState({
      ...this.state,
      segmentRules: [...this.state.segmentRules, rule],
    });
  };

  onPartingTimeUpdate = (enabled: boolean, schedule: any) => {
    const regionPublishMeta = this.state.regionPublishMeta;
    const currentRegionPublishMeta = regionPublishMeta.get(this.activeRegion);
    if (!enabled) {
      schedule = null;
    }
    const newPublishMeta: RegionPublishMeta = {
      ...currentRegionPublishMeta,
      partingSchedule: schedule,
      isPartedGlance: enabled,
    };
    regionPublishMeta.set(this.activeRegion, newPublishMeta);
    this.setState({
      ...this.state,
      regionPublishMeta: regionPublishMeta,
    });
  };

  render() {
    const draft = this.props.draft;
    const { isDspCampaign } = draft.dspCampaignData || { isDspCampaign: false };
    let sortedPreferedRegions = this.sortedPreferedRegions;
    let activeRegion = this.activeRegion;

    if (draft.preferedRegions && draft.preferedRegions.length > 0) {
      const newSortedPreferedRegions = draft.preferedRegions.sort((a, b) =>
        regionSort(a, b),
      );
      if (newSortedPreferedRegions != this.sortedPreferedRegions) {
        this.sortedPreferedRegions = newSortedPreferedRegions;
        sortedPreferedRegions = newSortedPreferedRegions;
        this.activeRegion = newSortedPreferedRegions[0];
        activeRegion = newSortedPreferedRegions[0];
      }
    }
    const currentRegionPublishMeta = sortedPreferedRegions
      ? this.state.regionPublishMeta.get(activeRegion)
      : null;

    const publishActions = [];
    publishActions.push(
      <FlatButton
        label="Cancel"
        primary={true}
        onClick={this.props.closeDialog}
      />,
    );

    const tabs = [];
    if (sortedPreferedRegions) {
      publishActions.push(
        <FlatButton
          label={'Publish for ' + Utils.getReadableRegionName(activeRegion)}
          primary={true}
          disabled={
            this.state.publishInProgress ||
            !sortedPreferedRegions ||
            !currentRegionPublishMeta.publishTo ||
            currentRegionPublishMeta.publishTo.length == 0
          }
          onClick={this.onPublishConfirm}
        />,
      );
      sortedPreferedRegions.forEach((region: Region) => {
        tabs.push(
          <Tab
            key={region}
            label={<span key={region}>{regionNames[region]}</span>}
            value={region}
            style={styles.regionTab}
          >
            <GlanceRegionPublish
              key={region}
              region={region}
              regionPublishMeta={currentRegionPublishMeta}
              defaultValues={this.defaultValues}
              userLevelDefaultValues={this.userLevelDefaultValues}
              partnerAdded={this.handlerPartnerAdd}
              partnerRemoved={this.handlerPartnerDelete}
              experimentAdded={this.handleExperimentSelected}
              variantSelected={this.handleExperimentVariantSelected}
              variantRemoved={this.handleExperimentVariantDelete}
              onDurationUpdate={this.durationUpdate}
              onStartTimeUpdate={this.startTimeUpdate}
              onUserLevelDurationUpdate={this.onUserLevelDurationUpdate}
              onPartingTimeUpdate={this.onPartingTimeUpdate}
              shortTitleCheck={this.state.shortTitleCheck}
              cheesySummaryCheck={this.state.cheesySummaryCheck}
              partnerMetas={this.props.studioAppConf.partnerMetas}
              draft={this.props.draft}
              experiments={this.props.experiments}
              segmentRules={this.state.segmentRules}
              onSegmentRuleSelected={this.onSegmentRuleSelected}
              addSegmentRuleFromGambit={this.onSegmentRuleFromGambit}
              onTargetingRuleDeleted={this.onTargetingRuleDeleted}
              isSettingsView={false}
              isDspCampaign={isDspCampaign}
            />
          </Tab>,
        );
      });
    }

    return (
      <div>
        <Dialog
          modal={false}
          open={this.props.open}
          onRequestClose={this.props.closeDialog}
          actions={publishActions}
          autoScrollBodyContent={true}
          contentStyle={{ maxWidth: '1200px', width: '90%' }}
        >
          {sortedPreferedRegions && this.state.loadState == LoadState.Loaded ? (
            <div>
              <Tabs value={activeRegion} onChange={this.handleActiveRegion}>
                {tabs}
              </Tabs>
            </div>
          ) : (
            <div>
              {!sortedPreferedRegions ? (
                <div
                  style={{ display: 'flex', margin: '0px auto', color: red500 }}
                >
                  {' '}
                  Add a region to draft first{' '}
                </div>
              ) : (
                ''
              )}
              {!(this.state.loadState == LoadState.Loaded) ? (
                <div style={{ display: 'flex', margin: '0px auto' }}>
                  {this.state.loadState == LoadState.Loading
                    ? 'Loading... '
                    : 'Loaded, but some error occured. Please try to refresh.'}
                </div>
              ) : (
                ''
              )}
            </div>
          )}
        </Dialog>
        <Snackbar
          open={this.state.snackBarOpen}
          message={this.state.snackBarMessage}
          onRequestClose={this.closeSnackbar}
        />
      </div>
    );
  }
}

export default GlancePublishComponent;



// WEBPACK FOOTER //
// ./src/pages/drafts/components/GlanceDraftPublish.tsx